import React from 'react';
import {
  COD_BUCKET_HINTS,
  COD_BUCKET_LABELS,
  COD_BUCKETS,
  COD_SUMMARY_KEYS,
  type CodBucket,
  type CodOrderSummary,
} from '@/services/api/codOrders';
import { formatRupiah } from './CodBadges';

/**
 * Sentinel for "no bucket filter".
 *
 * Not a sixth bucket — it is the ABSENCE of one, and the URL omits the param
 * entirely when it is selected. It lives here rather than on the page because
 * the cards are now what selects it: the Total card IS the "show everything"
 * control, and it is the default.
 */
export const ALL_BUCKETS = 'all' as const;
export type BucketFilter = CodBucket | typeof ALL_BUCKETS;
export const BUCKET_FILTERS: BucketFilter[] = [ALL_BUCKETS, ...COD_BUCKETS];

/**
 * Per-card colour + icon, as LITERAL class strings.
 *
 * Not composed (`bg-${tone}-lighter`) on purpose: Tailwind resolves classes by
 * scanning source text, so an interpolated name is never emitted and the card
 * silently renders untinted.
 *
 * `outstanding` and `pending_deposit` deliberately SHARE the warning tone —
 * colouring them differently would imply one is more urgent, which needs an
 * ageing threshold Ops and Finance have not agreed (see CodBadges). They are
 * told apart by their icons — who is holding the cash — not by hue.
 */
interface CardStyle {
  /** Always-visible left strip: what makes six cards distinguishable at a glance. */
  strip: string;
  /** Icon colour, and the tint the card takes when selected. */
  accent: string;
  selected: string;
  icon: string;
}

const TOTAL_STYLE: CardStyle = {
  strip: 'bg-primary-base',
  accent: 'text-primary-base',
  selected: 'border-primary-base bg-primary-alpha-10',
  icon: 'ri-wallet-3-line',
};

const BUCKET_STYLES: Record<CodBucket, CardStyle> = {
  in_delivery: {
    strip: 'bg-information-base',
    accent: 'text-information-base',
    selected: 'border-information-base bg-information-lighter',
    icon: 'ri-truck-line',
  },
  outstanding: {
    strip: 'bg-warning-base',
    accent: 'text-warning-base',
    selected: 'border-warning-base bg-warning-lighter',
    // The rider is holding cash they collected.
    icon: 'ri-hand-coin-line',
  },
  pending_deposit: {
    strip: 'bg-warning-base',
    accent: 'text-warning-base',
    selected: 'border-warning-base bg-warning-lighter',
    // The PIC is holding it, on its way to a bank.
    icon: 'ri-bank-line',
  },
  pending_finance: {
    strip: 'bg-warning-base',
    accent: 'text-warning-base',
    selected: 'border-warning-base bg-warning-lighter',
    // The money is claimed to be in the bank; Finance has yet to find it on a
    // statement.
    icon: 'ri-search-eye-line',
  },
  confirmed: {
    strip: 'bg-success-base',
    accent: 'text-success-base',
    selected: 'border-success-base bg-success-lighter',
    icon: 'ri-checkbox-circle-line',
  },
  failed_delivery: {
    strip: 'bg-error-base',
    accent: 'text-error-base',
    selected: 'border-error-base bg-error-lighter',
    // An attempt was lost, not the order. The arrow says it can come back.
    icon: 'ri-arrow-go-back-line',
  },
  not_applicable: {
    strip: 'bg-faded-base',
    accent: 'text-faded-base',
    selected: 'border-faded-base bg-faded-lighter',
    icon: 'ri-close-circle-line',
  },
};

/**
 * One summary card.
 *
 * Always a `<button>`, never a `<div>` that happens to have an onClick: the
 * whole set behaves identically, so nothing on this row looks static while its
 * neighbour filters. The affordances are deliberately layered, because the
 * previous version was six flat grey boxes that gave no hint they did anything:
 *
 *   - `cursor-pointer` and a hover lift (border + shadow) — the pointer signal
 *   - a chevron that is visible AT REST, not only on hover, so the card reads
 *     as interactive before the mouse arrives (and on touch, where hover
 *     never happens at all)
 *   - `active:` press feedback, and a `focus-visible` ring for the keyboard
 *   - `aria-pressed`, so a screen reader gets the same on/off state the tint
 *     conveys visually
 */
const SummaryCard: React.FC<{
  label: string;
  /** Extra word after the count. Omitted rather than padded with filler. */
  sublabel?: string;
  hint: string;
  amount: number;
  count: number;
  style: CardStyle;
  selected: boolean;
  loading: boolean;
  onSelect: () => void;
}> = ({
  label,
  sublabel,
  hint,
  amount,
  count,
  style,
  selected,
  loading,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    title={
      selected
        ? `${hint}\n(sedang ditampilkan)`
        : `${hint}\nKlik untuk memfilter daftar`
    }
    className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border pl-4 pr-3 py-3 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-alpha-24 focus-visible:ring-offset-1 active:scale-[0.99] ${
      selected
        ? `${style.selected} shadow-custom-sm`
        : 'border-stroke-soft-200 bg-bg-white-0 hover:border-stroke-sub-300 hover:shadow-custom-sm'
    }`}
  >
    {/* The accent strip. Full-strength when selected, muted at rest — present
        either way, so the card's identity does not depend on being picked. */}
    <span
      aria-hidden
      className={`absolute inset-y-0 left-0 w-1 ${style.strip} ${
        selected ? '' : 'opacity-40 group-hover:opacity-100'
      } transition-opacity`}
    />

    <div className="flex items-start justify-between gap-2">
      {/* `items-start`, not `items-center`: the label now wraps, and centring
          would drag the icon down beside the second line. */}
      {/* `min-h-8` = two lines at leading-4. Reserved whether the label needs
          them or not, so every figure in the row sits on the same baseline —
          without it a one-line card lifts its rupiah above its neighbours' and
          the row reads as ragged. */}
      <div className="flex min-h-8 min-w-0 items-start gap-1.5">
        <i
          className={`${style.icon} mt-px shrink-0 text-sm leading-4 ${style.accent}`}
        />
        {/* Wraps to two lines instead of truncating. A card labelled
            "MENUNGGU KONFIRM…" tells an operator nothing — and the three longest
            labels are the two new Leg 3 buckets and "Gagal kirim (bisa retry)",
            whose whole job is to be distinguishable from "Retur / gagal kirim".
            Two lines is the ceiling: `line-clamp-2` keeps a hypothetical third
            from pushing the figure out of alignment across the row, and every
            label we have fits in two at the narrowest card width. */}
        <span
          className={`line-clamp-2 text-[11px] font-medium uppercase leading-4 tracking-wider ${
            selected ? 'text-text-strong-950' : 'text-text-sub-600'
          }`}
        >
          {label}
        </span>
      </div>
      {/* Selected shows a check instead of the chevron: the card is the state,
          so it has to say "this is the one" and not just "clickable". */}
      <i
        aria-hidden
        className={`shrink-0 text-[16px] transition-transform ${
          selected
            ? `ri-check-line ${style.accent}`
            : 'ri-arrow-right-s-line text-text-sub-600 group-hover:translate-x-0.5 group-hover:text-text-strong-950'
        }`}
      />
    </div>

    {loading ? (
      <span className="mt-2 h-6 w-24 animate-pulse rounded bg-bg-weak-50" />
    ) : (
      // `truncate` + title: the exact figure matters to Finance, so a card too
      // narrow for `Rp 120.873.796` must not silently round or wrap it.
      <span
        className="mt-1.5 truncate text-base font-medium leading-6 tabular-nums text-text-strong-950"
        title={formatRupiah(amount)}
      >
        {formatRupiah(amount)}
      </span>
    )}

    <span className="mt-0.5 truncate text-[11px] text-text-sub-600">
      {loading ? ' ' : `${count} pesanan`}
      {!loading && sublabel ? ` · ${sublabel}` : ''}
    </span>
  </button>
);

/**
 * The bucket filter, as cards.
 *
 * Figures come from the same response as the rows, so a card can never disagree
 * with the table beneath it — and they always count across EVERY bucket, never
 * narrowed by the current selection, which is what makes them usable as
 * navigation rather than a readout of what you already filtered to.
 */
const CodSummaryCards: React.FC<{
  summary: CodOrderSummary;
  selected: BucketFilter;
  onSelect: (next: BucketFilter) => void;
  loading?: boolean;
}> = ({ summary, selected, onSelect, loading = false }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
    <SummaryCard
      label="Total COD"
      sublabel="semua bucket"
      hint="Seluruh pesanan COD pada rentang tanggal terpilih"
      amount={summary.total.amount}
      count={summary.total.count}
      style={TOTAL_STYLE}
      selected={selected === ALL_BUCKETS}
      loading={loading}
      // Always selects "all" rather than toggling: this card's job is to get
      // back to the unfiltered list, and it is already the default state.
      onSelect={() => onSelect(ALL_BUCKETS)}
    />
    {COD_BUCKETS.map((bucket) => (
      <SummaryCard
        key={bucket}
        label={COD_BUCKET_LABELS[bucket]}
        hint={COD_BUCKET_HINTS[bucket]}
        amount={summary[COD_SUMMARY_KEYS[bucket]].amount}
        count={summary[COD_SUMMARY_KEYS[bucket]].count}
        style={BUCKET_STYLES[bucket]}
        sublabel={selected === bucket ? 'ditampilkan' : undefined}
        selected={selected === bucket}
        loading={loading}
        // Clicking the selected bucket clears it, which lands on Total —
        // the same place the Total card goes.
        onSelect={() => onSelect(selected === bucket ? ALL_BUCKETS : bucket)}
      />
    ))}
  </div>
);

export default CodSummaryCards;
